import { useState } from 'react';
import { X, MessageCircle, Lightbulb, AlertTriangle, Send } from 'lucide-react';

interface AnnotationFormProps {
  pagina: number;
  posX: number;
  posY: number;
  onSubmit: (dados: { pagina: number; posX: number; posY: number; texto: string; tipo: string }) => Promise<void>;
  onCancel: () => void;
}

const tipos = [
  { key: 'comentario', label: 'Comentario', icon: MessageCircle, color: 'text-blue-600 border-blue-300 bg-blue-50' },
  { key: 'sugestao', label: 'Sugestao', icon: Lightbulb, color: 'text-amber-600 border-amber-300 bg-amber-50' },
  { key: 'problema', label: 'Problema', icon: AlertTriangle, color: 'text-red-600 border-red-300 bg-red-50' },
];

export function AnnotationForm({ pagina, posX, posY, onSubmit, onCancel }: AnnotationFormProps) {
  const [texto, setTexto] = useState('');
  const [tipo, setTipo] = useState('comentario');
  const [sending, setSending] = useState(false);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    if (!texto.trim() || sending) return;
    setSending(true);
    try {
      await onSubmit({ pagina, posX, posY, texto: texto.trim(), tipo });
    } finally {
      setSending(false);
    }
  };

  return (
    <div
      className="absolute z-30 bg-white dark:bg-gray-800 rounded-lg shadow-xl border border-gray-200 dark:border-gray-600 w-72"
      style={{
        left: `${Math.min(posX, 70)}%`,
        top: `${Math.min(posY, 70)}%`,
      }}
      onClick={(e) => e.stopPropagation()}
    >
      <div className="flex items-center justify-between px-3 py-2 border-b border-gray-100 dark:border-gray-700">
        <span className="text-xs font-medium text-gray-500 dark:text-gray-400">
          Nova anotacao - Pag. {pagina}
        </span>
        <button onClick={onCancel} className="text-gray-400 hover:text-gray-600 dark:hover:text-gray-300">
          <X className="w-4 h-4" />
        </button>
      </div>

      <form onSubmit={handleSubmit} className="p-3 space-y-3">
        {/* Tipo */}
        <div className="flex gap-1">
          {tipos.map(t => {
            const Icon = t.icon;
            return (
              <button
                key={t.key}
                type="button"
                onClick={() => setTipo(t.key)}
                className={`flex items-center gap-1 px-2 py-1 text-xs rounded border transition-colors ${
                  tipo === t.key ? t.color : 'border-gray-200 text-gray-500 hover:bg-gray-50 dark:border-gray-600 dark:hover:bg-gray-700'
                }`}
              >
                <Icon className="w-3 h-3" />
                {t.label}
              </button>
            );
          })}
        </div>

        {/* Texto */}
        <textarea
          value={texto}
          onChange={(e) => setTexto(e.target.value)}
          placeholder="Escreva sua anotacao..."
          rows={3}
          autoFocus
          className="w-full text-sm px-2 py-1.5 border border-gray-200 dark:border-gray-600 rounded bg-white dark:bg-gray-900 text-gray-900 dark:text-white placeholder-gray-400 focus:outline-none focus:ring-1 focus:ring-blue-500 resize-none"
        />

        {/* Submit */}
        <div className="flex justify-end">
          <button
            type="submit"
            disabled={!texto.trim() || sending}
            className="flex items-center gap-1.5 px-3 py-1.5 bg-blue-600 text-white text-xs font-medium rounded hover:bg-blue-700 disabled:opacity-50 disabled:cursor-not-allowed transition-colors"
          >
            <Send className="w-3.5 h-3.5" />
            {sending ? 'Enviando...' : 'Enviar'}
          </button>
        </div>
      </form>
    </div>
  );
}
